import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock } from "vue";
import { ref, watch, onMounted, computed, nextTick } from 'vue';
import LlSelect from '@/components/LlSelect.vue';
import { useMatterAssignedUsers } from '@/composables/useMatterAssignedUsers';
import { useInvoiceState } from '@/modules/billing/review/editDraft/useInvoiceState';
export default {
  __name: 'SelectUser',
  props: {
    modelValue: {
      type: [Object, String, Number]
    },
    density: {
      type: String,
      default: 'compact'
    },
    returnObject: {
      type: Boolean,
      default: true
    },
    placeholder: {
      type: String,
      default: 'User'
    },
    variant: {
      type: String,
      default: 'outlined'
    },
    hideDetails: {
      type: [Boolean, String],
      default: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    // New prop to enable matter-based user filtering
    useMatterFiltering: {
      type: Boolean,
      default: false
    },
    // Matter ID for filtering users
    matterId: {
      type: String,
      default: null
    },
    label: {
      type: String,
      default: 'User'
    },
    // Custom users list (overrides fetched users if provided)
    customUsers: {
      type: Array,
      default: null
    }
  },
  emits: ['update:modelValue'],
  setup(__props, {
    expose: __expose,
    emit: __emit
  }) {
    const props = __props;
    const emit = __emit;

    // Helper function to ensure user has proper display properties
    const ensureUserDisplayProperties = user => {
      if (!user || !user.id) return user;

      // Function to extract clean user name for display
      const extractUserName = user => {
        // Handle string name (most common case)
        if (typeof user.name === 'string' && user.name.trim() && user.name !== user.id) {
          return user.name;
        }

        // Handle firstName/lastName combination
        if (user.firstName && user.lastName) {
          const fullName = `${user.firstName} ${user.lastName}`;
          return fullName;
        }

        // Handle single name properties
        if (user.firstName) {
          return user.firstName;
        }
        if (user.lastName) {
          return user.lastName;
        }

        // Handle displayName as fallback (but not if it's just the ID)
        if (user.displayName && user.displayName !== user.id) {
          return user.displayName;
        }

        // Return email as last resort
        if (user.email) {
          return user.email;
        }

        // If we only have the ID, return a clean fallback
        const fallbackName = `User ${user.id.substring(0, 8)}...`;
        return fallbackName;
      };
      const extractedName = extractUserName(user);
      const result = {
        ...user,
        name: extractedName,
        displayName: extractedName
      };
      return result;
    };

    // Initialize internalValue with proper displayName if it's missing
    const internalValue = ref(ensureUserDisplayProperties(props.modelValue));

    // Get groups for backward compatibility (merging accounts from lineItems)
    const invoiceState = useInvoiceState();
    const {
      groups
    } = invoiceState;

    // Use the composable for all user management
    const {
      assignedUsers,
      loading,
      fetchMatterAssignedUsers,
      fetchAllAccounts
    } = useMatterAssignedUsers();

    // Computed property for available users
    const availableUsers = computed(() => {
      // Use customUsers if provided, otherwise use fetched assignedUsers
      const sourceUsers = props.customUsers || assignedUsers.value;
      // Ensure all users from source have proper display properties
      const users = sourceUsers.map(user => ensureUserDisplayProperties(user));

      // Check if current selection needs upgrading from fallback to real user
      if (internalValue.value?.id && internalValue.value.name?.startsWith('User ')) {
        const fullUser = users.find(user => user.id === internalValue.value.id);
        if (fullUser && fullUser.name && !fullUser.name.startsWith('User ')) {
          internalValue.value = fullUser;
          emit('update:modelValue', fullUser);
        }
      }

      // Ensure current selection is included in the list
      if (internalValue.value?.id) {
        const existingUser = users.find(user => user.id === internalValue.value.id);
        if (!existingUser) {
          users.unshift(ensureUserDisplayProperties(internalValue.value));
        }
      }
      return users;
    });

    // Watch for external model value changes
    watch(() => props.modelValue, newValue => {
      internalValue.value = newValue ? ensureUserDisplayProperties(newValue) : null;
    }, {
      immediate: true
    });

    // Watch for matter ID changes to refetch users
    watch(() => props.matterId, async newMatterId => {
      if (props.useMatterFiltering) {
        await fetchUsers();
      }
    });

    // Function to handle model update
    const handleUpdate = newValue => {
      internalValue.value = newValue;
      emit('update:modelValue', newValue);
    };

    // Function to get item title
    const getItemTitle = item => {
      return item.displayName || item.name;
    };

    // Function to fetch users
    const fetchUsers = async () => {
      if (props.useMatterFiltering && props.matterId) {
        await fetchMatterAssignedUsers(props.matterId);
      } else {
        await fetchAllAccounts(groups);
      }
    };

    // Expose a clear method for parent components
    const clearSelection = () => {
      internalValue.value = null;
    };

    // Fetch users on component mount
    onMounted(() => {
      fetchUsers();
    });

    // Expose methods to parent
    __expose({
      clearSelection
    });
    return (_ctx, _cache) => {
      return _openBlock(), _createBlock(LlSelect, {
        modelValue: internalValue.value,
        "onUpdate:modelValue": [_cache[0] || (_cache[0] = $event => internalValue.value = $event), handleUpdate],
        items: availableUsers.value,
        "item-title": getItemTitle,
        "item-value": "id",
        density: __props.density,
        "return-object": __props.returnObject,
        placeholder: __props.placeholder,
        variant: __props.variant,
        "hide-details": __props.hideDetails,
        clearable: true,
        disabled: __props.disabled || _unref(loading),
        isAutocomplete: "",
        label: __props.label,
        labelClass: "text-sm at-choose-client remove-outline",
        loading: _unref(loading)
      }, null, 8, ["modelValue", "items", "density", "return-object", "placeholder", "variant", "hide-details", "disabled", "label", "loading"]);
    };
  }
};